import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Facebook, Instagram, Linkedin, Twitter, Phone } from "lucide-react";
import logo from "@/assets/logo.png";
import { getSiteSettings } from "@/lib/api";
import { useTheme } from "@/components/ThemeProvider";

const SOCIAL_KEYS: { key: string; label: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  { key: "social_twitter", label: "X (Twitter)", Icon: Twitter },
  { key: "social_linkedin", label: "LinkedIn", Icon: Linkedin },
  { key: "social_facebook", label: "Facebook", Icon: Facebook },
  { key: "social_instagram", label: "Instagram", Icon: Instagram },
  {
    key: "social_pinterest",
    label: "Pinterest",
    Icon: ({ className }) => (
      <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M12 2C6.48 2 2 6.48 2 12c0 4.09 2.45 7.6 5.96 9.13-.08-.78-.16-1.97.03-2.82.18-.77 1.16-4.92 1.16-4.92s-.3-.6-.3-1.48c0-1.39.81-2.43 1.81-2.43.85 0 1.27.64 1.27 1.41 0 .86-.55 2.15-.83 3.34-.24 1 .5 1.82 1.49 1.82 1.79 0 3.16-1.89 3.16-4.61 0-2.41-1.73-4.1-4.21-4.1-2.87 0-4.55 2.15-4.55 4.37 0 .87.33 1.79.75 2.3.08.1.09.19.07.29-.08.32-.25 1-.28 1.14-.04.18-.15.22-.34.13-1.27-.59-2.06-2.45-2.06-3.94 0-3.21 2.33-6.16 6.72-6.16 3.53 0 6.27 2.51 6.27 5.87 0 3.5-2.21 6.32-5.27 6.32-1.03 0-2-.54-2.33-1.17l-.63 2.41c-.23.88-.85 1.99-1.27 2.66.96.3 1.97.46 3.03.46 5.52 0 10-4.48 10-10S17.52 2 12 2z" />
      </svg>
    ),
  },
];

const Footer = () => {
  const [socials, setSocials] = useState<Record<string, string>>({});
  const { resolved } = useTheme();
  const activeLogo = resolved === "dark"
    ? (socials.site_logo_dark_url || socials.site_logo_url)
    : (socials.site_logo_url || socials.site_logo_dark_url);
  const useCustomLogo = !!activeLogo;

  useEffect(() => {
    getSiteSettings().then(setSocials).catch(() => setSocials({}));
  }, []);

  const visibleSocials = SOCIAL_KEYS.filter(({ key }) => (socials[key] || "").trim().length > 0);

  return (
    <footer className="border-t border-border/50 bg-background py-16">
      <div className="container">
        <div className="grid gap-10 md:grid-cols-4">
          <div>
            <img
              src={activeLogo || logo}
              alt="PDNInvestor"
              width={256}
              height={256}
              loading="lazy"
              decoding="async"
              style={{ height: `${Math.min(160, Math.max(24, Number(socials.site_logo_height_desktop) || 64))}px`, width: "auto" }}
              className={`w-auto ${useCustomLogo ? "" : "dark:invert"}`}
            />
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-xs">
              Premium domain names for visionary brands building the future.
            </p>
            {visibleSocials.length > 0 && (
              <div className="mt-5 flex items-center gap-2">
                {visibleSocials.map(({ key, label, Icon }) => (
                  <a
                    key={key}
                    href={socials[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/60 text-muted-foreground hover:text-foreground hover:border-foreground/40 hover:bg-muted transition-colors"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            )}
          </div>
          <div>
            <p className="font-display text-sm font-semibold text-foreground uppercase tracking-wider mb-4">Navigate</p>
            <nav className="flex flex-col gap-3 text-sm">
              <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Home</Link>
              <Link to="/domains" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Domains</Link>
              <Link to="/sales" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Sales</Link>
              <Link to="/blog" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Blog</Link>
              <Link to="/about" className="text-muted-foreground hover:text-foreground transition-colors duration-200">About</Link>
              <Link to="/contact" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Contact</Link>
              <Link to="/faq" className="text-muted-foreground hover:text-foreground transition-colors duration-200">FAQ</Link>
            </nav>
          </div>
          <div>
            <p className="font-display text-sm font-semibold text-foreground uppercase tracking-wider mb-4">Tools</p>
            <nav className="flex flex-col gap-3 text-sm">
              
              <Link to="/bulk-inquiry" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Bulk Inquiry</Link>
              <Link to="/escrow" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Escrow Process</Link>
              <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Privacy Policy</Link>
              <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Terms of Use</Link>
            </nav>
          </div>
          <div>
            <p className="font-display text-sm font-semibold text-foreground uppercase tracking-wider mb-4">Contact</p>
            <div className="flex flex-col gap-3 text-sm">
              <a
                href="mailto:Contact@pdninvestor.com"
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors duration-200"
              >
                <Mail className="h-4 w-4" />
                Contact@pdninvestor.com
              </a>
              {(socials.whatsapp_number || "").trim().length > 0 && (
                <a
                  href={`https://wa.me/${socials.whatsapp_number.replace(/[^0-9+]/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors duration-200"
                >
                  <Phone className="h-4 w-4" />
                  WhatsApp: {socials.whatsapp_number}
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="mt-14 border-t border-border/50 pt-8 text-center text-xs text-muted-foreground">
          &copy; 2026 PDNInvestor.com. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

export default Footer;
